'use client'

import type React from 'react'
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'

type WaitlistSignupContextValue = {
  isOpen: boolean
  openKey: number
  productInterest: string | null
  open: (productInterest?: string) => void
  close: () => void
  isCurrentOpening: (openKey: number) => boolean
}

const WaitlistSignupContext = createContext<WaitlistSignupContextValue | null>(null)

export function WaitlistSignupProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [openKey, setOpenKey] = useState(0)
  const [productInterest, setProductInterest] = useState<string | null>(null)
  const latestOpenKey = useRef(0)
  const activeOpenKey = useRef<number | null>(null)

  const open = useCallback((interest?: string) => {
    const nextOpenKey = latestOpenKey.current + 1
    latestOpenKey.current = nextOpenKey
    activeOpenKey.current = nextOpenKey
    setProductInterest(interest ?? null)
    setOpenKey(nextOpenKey)
    setIsOpen(true)
  }, [])
  const close = useCallback(() => {
    activeOpenKey.current = null
    setIsOpen(false)
  }, [])
  const isCurrentOpening = useCallback(
    (candidateOpenKey: number) => activeOpenKey.current === candidateOpenKey,
    []
  )

  const value = useMemo(
    () => ({ isOpen, openKey, productInterest, open, close, isCurrentOpening }),
    [close, isCurrentOpening, isOpen, openKey, open, productInterest]
  )

  return <WaitlistSignupContext.Provider value={value}>{children}</WaitlistSignupContext.Provider>
}

export function useWaitlistSignupOptional() {
  return useContext(WaitlistSignupContext)
}

export function useWaitlistSignup() {
  const ctx = useContext(WaitlistSignupContext)

  if (!ctx) {
    throw new Error('useWaitlistSignup must be used within WaitlistSignupProvider')
  }

  return ctx
}
